import { Grid, Pagination, Typography, Container, Box } from '@mui/material';
import { BatchBox } from './BatchBox';
import { StyledEngineProvider } from '@mui/material/styles';
import { Storage, API } from 'aws-amplify';
import { getPrizesForConfig } from '../../../graphql/queries';
import { useState, useEffect, ChangeEvent } from 'react';
import { chunker } from '../../../pages/AdminPanel/Permissions/adminPanelUtils';
import '../../../common/GridWithPagination/GridWithPagination.css';

interface luckyNumbersExportProps {
  configurationId: string;
  proofType: string;
}

export interface ExportFile {
  key: string;
  url: string;
}

export interface BatchExportState {
  [batchId: string]: {
    inProgress: boolean;
    perBatchExports: ExportFile[];
    expectedCount?: number;
  };
}

const ManageLuckyNumbersExport = ({ configurationId, proofType }: luckyNumbersExportProps): JSX.Element => {
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [exports, setExports] = useState<BatchExportState>({});
  const [batchDataToDisplay, setBatchDataToDisplay] = useState<any[][]>([]);
  const itemsPerPage = 6;

  const fetchExports = async (inputBatchId: string) => {
    try {
      const prefix = `dataExporter/mechanicSpecificExports/${configurationId}#${inputBatchId}/completedExports/`;
      const files = await Storage.list(prefix, { level: 'public' });

      const hasFlag = files.some((file) => file.key.endsWith('in_progress_flag'));

      const exportFiles = await Promise.all(
        files
          .filter((file) => file.size > 0 && !file.key.endsWith('in_progress_flag'))
          .map(async (file) => {
            const url = await Storage.get(file.key, { level: 'public' });
            return { key: file.key, url };
          })
      );
      const currentCount = exportFiles.length;

      setExports((prev) => {
        const prevBatch = prev[inputBatchId] || { inProgress: false, perBatchExports: [], expectedCount: undefined };
        const expectedCount = prevBatch.expectedCount;
        const stillInProgress = hasFlag || (typeof expectedCount === 'number' && currentCount < expectedCount);

        return {
          ...prev,
          [inputBatchId]: {
            inProgress: stillInProgress,
            perBatchExports: exportFiles,
            expectedCount: stillInProgress ? expectedCount : undefined,
          },
        };
      });
    } catch (error) {
      console.error('Error fetching export files:', error);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      Object.keys(exports).forEach(async (batchId) => {
        if (exports[batchId].inProgress) {
          await fetchExports(batchId);
        }
      });
    }, 500);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exports]);

  useEffect(() => {
    const getBatches = async () => {
      const queryResult: any = await API.graphql({
        query: getPrizesForConfig,
        variables: {
          configuration_id: configurationId,
        },
      });
      return queryResult?.data?.getPrizesForConfig?.items || [];
    };

    const fetchData = async () => {
      const batches = await getBatches();
      await Promise.all(
        batches.map(async (batch: any) => {
          await fetchExports(batch.prize_id);
        })
      );
      const chunked = chunker(batches, itemsPerPage);
      setBatchDataToDisplay(chunked);
    };

    fetchData().catch((error) => {
      console.error('Error fetching lucky number batches with error:', error);
    });
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurationId]);

  const handlePageChange = (event: ChangeEvent<unknown>, page: number): void => {
    setCurrentPageNumber(page);
  };

  const pageIndex = currentPageNumber - 1;
  const currentPageItems = batchDataToDisplay[pageIndex] || [];

  return (
    <StyledEngineProvider injectFirst>
      <Typography variant="h3">Lucky Numbers Export</Typography>
      <Container maxWidth="lg" background-color="white" className="containerStyles">
        <Box className="boxGridItems">
          <Grid container columnSpacing={1} rowSpacing={2}>
            {currentPageItems.map((item: any, boxIndex: number) => (
              <Grid
                item
                key={`campaign-card-${boxIndex}`}
                sx={{
                  maxWidth: 470,
                  flexBasis: '470px',
                  width: '100%',
                }}
                data-testid={`campaign-card-${boxIndex}`}
              >
                <BatchBox
                  inputConfigurationId={item.configuration_id}
                  inputBatchId={item.prize_id}
                  inputBatchOrder={item.priority}
                  inputBatchTotalAvailable={item.total_available}
                  setExports={setExports}
                  inputBatchTotalAmount={item.total_amount}
                  inputExport={exports[item.prize_id]?.perBatchExports || []}
                  inputProofType={proofType}
                  exportInProgress={exports[item.prize_id]?.inProgress || false}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
        <Box>
          <Typography className="totalItemsLabelStyles">{batchDataToDisplay.flat().length} Total Items</Typography>
        </Box>
        {batchDataToDisplay.length > 1 && (
          <Pagination
            className="paginationBarStyles"
            count={batchDataToDisplay.length}
            showFirstButton={true}
            showLastButton={true}
            siblingCount={0}
            boundaryCount={1}
            page={currentPageNumber}
            onChange={handlePageChange}
          />
        )}
      </Container>
    </StyledEngineProvider>
  );
};

export { ManageLuckyNumbersExport };
